import { validateEmail } from './validation';

export interface ValidationRule {
  type: 'number' | 'text' | 'url' | 'color' | 'email' | 'select' | 'boolean';
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  options?: any[];
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Setting-specific validation rules based on key
const settingRules: Record<string, ValidationRule> = {
  'max_users_per_room': { type: 'number', min: 2, max: 100, required: true },
  'site_title': { type: 'text', required: true, minLength: 1, maxLength: 100 },
  'site_description': { type: 'text', required: true, minLength: 10, maxLength: 500 },
  'primary_color': { type: 'color', required: true },
  'logo_url': { type: 'url', required: false },
  'favicon_url': { type: 'url', required: false },
  'google_analytics_id': { type: 'text', required: false, maxLength: 50 },
  'contact_email': { type: 'email', required: true },
  'support_email': { type: 'email', required: false },
  'session_timeout_minutes': { type: 'number', min: 5, max: 1440, required: true },
  'max_login_attempts': { type: 'number', min: 3, max: 10, required: true },
  'puzzle_time_limit_seconds': { type: 'number', min: 30, max: 600, required: true },
};

export const validateNumberSetting = (
  value: any,
  min?: number,
  max?: number,
  required: boolean = false
): ValidationResult => {
  if (value === null || value === undefined || value === '') {
    if (required) {
      return { isValid: false, error: 'This field is required' };
    }
    return { isValid: true };
  }

  const num = Number(value);

  if (isNaN(num) || !isFinite(num)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }

  if (min !== undefined && num < min) {
    return { isValid: false, error: `Value must be at least ${min}` };
  }

  if (max !== undefined && num > max) {
    return { isValid: false, error: `Value must be no more than ${max}` };
  }

  return { isValid: true };
};

export const validateTextSetting = (
  value: any,
  required: boolean = false,
  minLength?: number,
  maxLength?: number
): ValidationResult => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    if (required) {
      return { isValid: false, error: 'This field is required and cannot be empty' };
    }
    return { isValid: true };
  }

  const str = String(value).trim();

  // Check for control characters
  if (/[\x00-\x1F\x7F]/.test(str)) {
    return { isValid: false, error: 'Contains invalid characters' };
  }

  if (minLength !== undefined && str.length < minLength) {
    return { isValid: false, error: `Must be at least ${minLength} characters` };
  }

  if (maxLength !== undefined && str.length > maxLength) {
    return { isValid: false, error: `Must be no more than ${maxLength} characters` };
  }

  return { isValid: true };
};

export const validateUrlSetting = (
  value: string,
  required: boolean = false
): ValidationResult => {
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'URL is required' };
    }
    return { isValid: true };
  }

  const trimmedValue = value.trim();

  // Allow relative URLs
  if (trimmedValue.startsWith('/')) {
    return { isValid: true };
  }

  // Validate absolute URLs
  try {
    const url = new URL(trimmedValue);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return { isValid: false, error: 'URL must use http or https protocol' };
    }
    return { isValid: true };
  } catch {
    return { isValid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
  }
};

export const validateColorSetting = (value: string): ValidationResult => {
  if (!value || value.trim() === '') {
    return { isValid: false, error: 'Color is required' };
  }

  const hexColorRegex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
  
  if (!hexColorRegex.test(value.trim())) {
    return { isValid: false, error: 'Color must be in hex format (e.g., #2563eb)' };
  }

  return { isValid: true };
};

export const validateSelectSetting = (
  value: any,
  options: any[]
): ValidationResult => {
  if (!options || options.length === 0) {
    return { isValid: true };
  }

  const isValid = options.includes(value);
  
  if (!isValid) {
    return { isValid: false, error: 'Please select a valid option' };
  }

  return { isValid: true };
};

export const validateEmailSetting = (
  value: string,
  required: boolean = false
): ValidationResult => {
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Email is required' };
    }
    return { isValid: true };
  }

  const emailValidation = validateEmail(value.trim());
  return emailValidation;
};

export const validateSettingValue = (
  setting: { key: string; setting_type: string; options?: any[] },
  value: any
): ValidationResult => {
  const rule = settingRules[setting.key];

  // If we have a specific rule for this setting, use it
  if (rule) {
    switch (rule.type) {
      case 'number':
        return validateNumberSetting(value, rule.min, rule.max, rule.required);
      case 'text':
        return validateTextSetting(value, rule.required, rule.minLength, rule.maxLength);
      case 'url':
        return validateUrlSetting(value, rule.required);
      case 'color':
        return validateColorSetting(value);
      case 'email':
        return validateEmailSetting(value, rule.required);
      case 'select':
        return validateSelectSetting(value, rule.options || setting.options || []);
      case 'boolean':
        return { isValid: true }; // Boolean values are always valid
    }
  }

  // Fall back to generic validation based on setting_type
  switch (setting.setting_type) {
    case 'number':
      return validateNumberSetting(value, 0); // Default: no negatives
    case 'text':
    case 'textarea':
      // Check if it's a description field (longer max length)
      const isDescription = setting.key.toLowerCase().includes('description');
      return validateTextSetting(value, false, undefined, isDescription ? 1000 : 500);
    case 'url':
      return validateUrlSetting(value, false);
    case 'color':
      return validateColorSetting(value);
    case 'email':
      return validateEmailSetting(value, false);
    case 'select':
      return validateSelectSetting(value, setting.options || []);
    case 'boolean':
      return { isValid: true };
    default:
      return { isValid: true };
  }
};
